import { useQuery } from '@tanstack/react-query';
import { issuesApi } from '../api/issues';
import { sendgridApi } from '../api/sendgrid';
import { useWebSocket } from '../hooks/useWebSocket';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';

function ContactStatCard({ label, value, delta, invertDelta = false, color }) {
  const colorClasses = {
    gray: 'bg-gray-50 border-gray-200 text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100',
    green: 'bg-green-50 border-green-200 text-green-900 dark:bg-green-900/30 dark:border-green-800/50 dark:text-green-100',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-900 dark:bg-yellow-900/30 dark:border-yellow-800/50 dark:text-yellow-100',
    red: 'bg-red-50 border-red-200 text-red-900 dark:bg-red-900/30 dark:border-red-800/50 dark:text-red-100',
    blue: 'bg-blue-50 border-blue-200 text-blue-900 dark:bg-blue-900/30 dark:border-blue-800/50 dark:text-blue-100',
  };

  const deltaColor = delta == null || delta === 0
    ? 'text-gray-400 dark:text-gray-500'
    : (invertDelta ? delta < 0 : delta > 0)
      ? 'text-green-600 dark:text-green-400'
      : 'text-red-500 dark:text-red-400';

  return (
    <div className={`rounded-lg border p-4 ${colorClasses[color] || colorClasses.gray}`}>
      <p className="text-xs font-medium uppercase tracking-wide opacity-70">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{value != null ? value.toLocaleString() : '-'}</p>
      {delta != null && (
        <p className={`mt-1 text-xs font-medium flex items-center gap-0.5 ${deltaColor}`}>
          {delta === 0 ? (
            <span>&ndash; 7d</span>
          ) : (
            <>
              {delta > 0 ? <ArrowUpIcon className="h-3 w-3" /> : <ArrowDownIcon className="h-3 w-3" />}
              {Math.abs(delta).toLocaleString()} / 7d
            </>
          )}
        </p>
      )}
    </div>
  );
}

function DashboardPage() {
  const navigate = useNavigate();
  const { lastMessage } = useWebSocket();
  const [notifications, setNotifications] = useState([]);
  
  const { data: issuesData } = useQuery({
    queryKey: ['issues', 'recent'],
    queryFn: () => issuesApi.list({ limit: 5 }),
  });

  const { data: contactStatsData } = useQuery({
    queryKey: ['contactStats', 'dashboard'],
    queryFn: () => sendgridApi.listContactStats({ limit: 8 }),
    staleTime: 1000 * 60 * 30,
  });

  const contactStats = contactStatsData?.stats || [];
  const csLatest = contactStats[0] || null;
  const cs7dAgo = contactStats.length >= 8 ? contactStats[7] : null;
  const csDelta = csLatest && cs7dAgo ? {
    totalContacts: csLatest.totalContacts - cs7dAgo.totalContacts,
    subscribed: csLatest.subscribed - cs7dAgo.subscribed,
    unsubscribed: csLatest.unsubscribed - cs7dAgo.unsubscribed,
    bounced: csLatest.bounced - cs7dAgo.bounced,
    newSubscribers: contactStats.slice(0, 7).reduce((sum, d) => sum + (d.newSubscribers || 0), 0),
  } : null;
  
  useEffect(() => {
    if (lastMessage && lastMessage.type !== 'connected' && lastMessage.type !== 'subscribed') {
      setNotifications((prev) => [
        {
          id: Date.now(),
          message: lastMessage,
          timestamp: new Date().toISOString(),
        },
        ...prev.slice(0, 4),
      ]);
    }
  }, [lastMessage]);
  
  return (
    <div className="px-4 py-6 sm:px-0">
      <h1 className="text-2xl font-bold text-obn-blue dark:text-white mb-6">Dashboard</h1>

      {/* Contact Stats */}
      {csLatest && (
        <div>
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Contact Stats</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <ContactStatCard label="Total Contacts" value={csLatest.totalContacts} delta={csDelta?.totalContacts} color="gray" />
            <ContactStatCard label="Subscribed" value={csLatest.subscribed} delta={csDelta?.subscribed} color="green" />
            <ContactStatCard label="Unsub (7d)" value={csDelta?.unsubscribed} invertDelta color="yellow" />
            <ContactStatCard label="Bounced (7d)" value={csDelta?.bounced} invertDelta color="red" />
            <ContactStatCard label="New (7d)" value={csDelta?.newSubscribers} color="blue" />
          </div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="bg-white dark:bg-gray-800 shadow dark:shadow-lg dark:shadow-black/20 dark:ring-1 dark:ring-white/10 rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-obn-blue dark:text-white mb-4">Recent Issues</h3>
            {issuesData?.issues?.length > 0 ? (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {issuesData.issues.map((issue) => (
                  <li key={issue.id} className="py-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <button
                          onClick={() => navigate(`/issues/${issue.issueNumber}`)}
                          className="text-sm font-medium text-obn-blue dark:text-blue-400 hover:text-obn-blue/80 dark:hover:text-blue-300 hover:underline focus:outline-none focus:underline text-left transition-colors duration-200"
                        >
                          #{issue.issueNumber} - {issue.title}
                        </button>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {new Date(issue.created).toLocaleDateString()}
                        </p>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ml-3 ${
                        issue.status === 'published'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200'
                      }`}>
                        {issue.status === 'published' ? 'Published' : 'Draft'}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No issues yet</p>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow dark:shadow-lg dark:shadow-black/20 dark:ring-1 dark:ring-white/10 rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-obn-blue dark:text-white mb-4">Real-time Notifications</h3>
            {notifications.length > 0 ? (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {notifications.map((notification) => (
                  <li key={notification.id} className="py-3">
                    <div className="flex justify-between">
                      <div>
                        <p className="text-sm font-medium text-obn-blue dark:text-blue-400">
                          {notification.message.type}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(notification.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No notifications yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;